import { createFileRoute, getRouteApi, Link } from '@tanstack/react-router';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Eye, Calendar } from 'lucide-react';
import { getEventParticipants } from '@/server/registration';

const dashboardRoute = getRouteApi('/admin/dashboard/event/$eventId');

export const Route = createFileRoute('/admin/dashboard/event/$eventId/')({
  component: OverviewPage,
  loader: async ({ params }) => {
    const { totalCount } = await getEventParticipants({
      data: params.eventId,
    });
    return { participantCount: totalCount };
  },
});

/** Formats a date for the overview display. */
function formatOverviewDate(date: Date | string | null): string {
  if (!date) return 'TBD';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Dashboard overview page showing key event metrics at a glance. */
function OverviewPage() {
  const { event } = dashboardRoute.useLoaderData();
  const { participantCount } = Route.useLoaderData();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">{event.name}</h2>
        {event.location && (
          <p className="text-sm text-gray-500 mt-1">{event.location}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">
                Participants
              </span>
            </div>
            <p className="text-2xl font-semibold text-gray-900">
              {participantCount}
            </p>
            <Link
              to="/admin/dashboard/event/$eventId/participants"
              params={{ eventId: event.id }}
            >
              <Button variant="outline" size="sm" className="mt-3">
                View Participants
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Eye className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">
                Page Views
              </span>
            </div>
            <p className="text-2xl font-semibold text-gray-900">—</p>
            <p className="text-xs text-gray-400 mt-1">Coming soon</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Calendar className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">
                Event Date
              </span>
            </div>
            <p className="text-2xl font-semibold text-gray-900">
              {formatOverviewDate(event.startDate)}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
